import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Box,
  Button,
  IconButton,
  Avatar,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ACTION_ICON_COLORS } from '../constants/iconActionColors';
import * as endPoint from '../assets/js/endpoints';
import Loader from '../components/loader';
import DataTable from '../components/DataTable';
import PageTitle from '../components/PageTitle';
import { useConfirm } from '../assets/contexts/confirmContext';
import { useNotification } from '../assets/contexts/notificationContext';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { INTRANET_PAGE_CONTAINER_SX } from '../constants/layout';
import * as utils from '../assets/js/utils';
import AboutOrderSelectedDnD from './AboutOrderSelectedDnD';

const isAdminUser = user => user.roles?.some(role => role.name === 'admin');
const sortByOrderThenName = (a, b) => {
  const aOrder = Number(a.employee_order);
  const bOrder = Number(b.employee_order);
  const aHasOrder = Number.isFinite(aOrder) && aOrder > 0;
  const bHasOrder = Number.isFinite(bOrder) && bOrder > 0;

  if (aHasOrder && bHasOrder) return aOrder - bOrder;
  if (aHasOrder) return -1;
  if (bHasOrder) return 1;

  return `${a.name || ''} ${a.last_name || a.lastName || ''}`.localeCompare(
    `${b.name || ''} ${b.last_name || b.lastName || ''}`,
    'es',
    { sensitivity: 'base' }
  );
};

export default function Usuarios() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userEdits, setUserEdits] = useState({});
  const [initialUserEdits, setInitialUserEdits] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [deleteTargetUserId, setDeleteTargetUserId] = useState(null);
  const [transferCandidates, setTransferCandidates] = useState([]);
  const [transferToUserIdInput, setTransferToUserIdInput] = useState('');
  const [selectedDnDIds, setSelectedDnDIds] = useState([]);
  const [selectedDnDOrderIds, setSelectedDnDOrderIds] = useState([]);
  const [advancedModeEnabled, setAdvancedModeEnabled] = useState(false);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showNotification } = useNotification();

  const fetchUsers = useCallback(async () => {
    try {
      const data = await endPoint.getAllUsers();
      const normalizedUsers = Array.isArray(data) ? data : data.data;
      const initialMap = Object.fromEntries(
        normalizedUsers.map(u => [u.id, {
          employee_order: u.employee_order ?? '',
          active: u.active !== false,
          show_in_about: u.show_in_about !== false,
        }])
      );
      setUsers(normalizedUsers);
      setUserEdits(initialMap);
      setInitialUserEdits(initialMap);
    } catch (_error) {
      showNotification('Error al obtener usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const aboutEmployees = useMemo(
    () => users.filter(user => user.id !== 1),
    [users]
  );
  const usersWithoutRoot = useMemo(
    () => users.filter(user => user.id !== 1),
    [users]
  );

  useEffect(() => {
    if (aboutEmployees.length === 0) {
      setSelectedDnDIds([]);
      setSelectedDnDOrderIds([]);
      return;
    }

    const shownEmployees = aboutEmployees
      .filter(user => Boolean(userEdits[user.id]?.show_in_about))
      .sort((a, b) => sortByOrderThenName(
        { ...a, employee_order: userEdits[a.id]?.employee_order },
        { ...b, employee_order: userEdits[b.id]?.employee_order }
      ));

    const shownIds = shownEmployees.map(user => user.id);

    setSelectedDnDIds(shownIds);
    setSelectedDnDOrderIds([
      ...shownIds,
      ...aboutEmployees.map(user => user.id).filter(id => !shownIds.includes(id))
    ]);
  }, [aboutEmployees, userEdits]);

  const aboutUsersCount = useMemo(
    () => usersWithoutRoot.filter(user => (userEdits[user.id]?.show_in_about ?? user.show_in_about !== false)).length,
    [usersWithoutRoot, userEdits]
  );

  const modifiedEntries = useMemo(
    () => usersWithoutRoot.filter(user => {
      const current = userEdits[user.id] ?? {};
      const initial = initialUserEdits[user.id] ?? {};
      return (
        String(current.employee_order ?? '') !== String(initial.employee_order ?? '') ||
        Boolean(current.active) !== Boolean(initial.active) ||
        Boolean(current.show_in_about) !== Boolean(initial.show_in_about)
      );
    }),
    [usersWithoutRoot, userEdits, initialUserEdits]
  );

  const handleEdit = id => navigate(`/admin/usuarios/editar/${id}`);

  const handleDelete = async id => {
    const candidates = usersWithoutRoot.filter(user => user.id !== id);
    if (candidates.length === 0) {
      showNotification('No hay usuarios disponibles para reasignar datos.', 'warning');
      return;
    }
    setDeleteTargetUserId(id);
    setTransferCandidates(candidates);
    setTransferToUserIdInput('');
  };

  const closeTransferDialog = () => {
    setDeleteTargetUserId(null);
    setTransferCandidates([]);
    setTransferToUserIdInput('');
  };

  const confirmDeleteWithTransfer = async () => {
    const transferToUserId = Number(transferToUserIdInput);
    const validChoice = Number.isInteger(transferToUserId) && transferCandidates.some(user => user.id === transferToUserId);

    if (!validChoice || deleteTargetUserId === null) {
      showNotification('ID de usuario inválido para reasignación.', 'error');
      return;
    }

    showNotification(
      'Atención: eliminar este usuario puede afectar datos relacionados (citas, clientes, servicios, permisos).',
      'warning'
    );

    const ok = await confirm('¿Eliminar usuario?');
    if (!ok) return;

    try {
      await endPoint.deleteUser(deleteTargetUserId, transferToUserId);
      fetchUsers();
      showNotification('Usuario eliminado', 'success');
      closeTransferDialog();
    } catch (error) {
      const message = error?.response?.data?.message || 'Error eliminando usuario';
      showNotification(message, 'error');
    }
  };

  const handleFieldChange = (user, field, nextValue) => {
    if (user.id === 1) return;

    if (field === 'employee_order') {
      if (nextValue === '') {
        setUserEdits(prev => ({ ...prev, [user.id]: { ...prev[user.id], employee_order: '' } }));
        return;
      }

      const parsed = Number(nextValue);
      if (!Number.isInteger(parsed) || parsed < 1) return;

      setUserEdits(prev => ({ ...prev, [user.id]: { ...prev[user.id], employee_order: parsed } }));
      return;
    }

    setUserEdits(prev => ({ ...prev, [user.id]: { ...prev[user.id], [field]: nextValue } }));
  };

  const validateBatch = () => {
    const maxOrder = Math.max(aboutUsersCount, 1);
    const usedNumbers = new Map();

    for (const user of usersWithoutRoot) {
      const current = userEdits[user.id] ?? {};
      const isShownInAbout = Boolean(current.show_in_about);
      const rawValue = current.employee_order;

      if (!isShownInAbout || rawValue === '' || rawValue === null || rawValue === undefined) continue;

      const value = Number(rawValue);
      if (!Number.isInteger(value) || value < 1 || value > maxOrder) {
        return `El orden de ${user.name} debe estar entre 1 y ${aboutUsersCount}.`;
      }

      if (usedNumbers.has(value)) {
        const prevUser = usedNumbers.get(value);
        return `Conflicto: ${prevUser} y ${user.name} tienen el mismo orden ${value}.`;
      }

      usedNumbers.set(value, user.name);
    }

    return null;
  };

  const handleSaveAll = async () => {
    if (modifiedEntries.length === 0) {
      showNotification('No hay cambios para guardar.', 'info');
      return;
    }

    const validationError = validateBatch();
    if (validationError) {
      showNotification(validationError, 'warning');
      return;
    }

    try {
      setSavingAll(true);

      const runUpdate = (user, employeeOrderOverride) => {
        const current = userEdits[user.id] ?? {};
        const employeeOrder =
          employeeOrderOverride !== undefined
            ? employeeOrderOverride
            : (current.employee_order === '' ? '' : Number(current.employee_order));

        return endPoint.updateUser(
          user.id,
          user.name,
          user.last_name || user.lastName || '',
          user.email,
          user.roles?.[0]?.name || '',
          '',
          user.bio || '',
          null,
          user.category_id || user.category?.id || '',
          user.services?.[0]?.id || '',
          user.color || '',
          user.featured === true,
          employeeOrder,
          false,
          current.active,
          current.show_in_about
        );
      };

      const usersWithOrderChanges = modifiedEntries.filter(user => {
        const current = userEdits[user.id] ?? {};
        const initial = initialUserEdits[user.id] ?? {};
        return (
          Boolean(current.show_in_about) &&
          String(current.employee_order ?? '') !== String(initial.employee_order ?? '')
        );
      });

      for (const user of usersWithOrderChanges) {
        await runUpdate(user, '');
      }

      for (const user of modifiedEntries) {
        await runUpdate(user);
      }

      showNotification('Cambios guardados correctamente.', 'success');
      await fetchUsers();
    } catch (error) {
      const message = error?.response?.data?.message || 'No se pudieron guardar todos los cambios.';
      showNotification(message, 'error');
    } finally {
      setSavingAll(false);
    }
  };

  const orderedAboutIds = useMemo(
    () => aboutEmployees
      .filter(user => Boolean(userEdits[user.id]?.show_in_about))
      .sort((a, b) => sortByOrderThenName(
        { ...a, employee_order: userEdits[a.id]?.employee_order },
        { ...b, employee_order: userEdits[b.id]?.employee_order }
      ))
      .map(user => user.id),
    [aboutEmployees, userEdits]
  );

  const moveByTableRowDrag = (draggedUserId, targetUserId) => {
    const fromIndex = orderedAboutIds.indexOf(Number(draggedUserId));
    const toIndex = orderedAboutIds.indexOf(Number(targetUserId));

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const reordered = [...orderedAboutIds];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    setUserEdits(prev => {
      const next = { ...prev };
      reordered.forEach((id, index) => {
        next[id] = {
          ...next[id],
          show_in_about: true,
          employee_order: index + 1
        };
      });
      return next;
    });
  };

  const applySelectedDnDOrder = () => {
    const selectedSet = new Set(selectedDnDIds);
    const orderedSelectedIds = selectedDnDOrderIds.filter(id => selectedSet.has(id));

    setUserEdits(prev => {
      const next = { ...prev };

      aboutEmployees.forEach(user => {
        const isSelected = selectedSet.has(user.id);

        next[user.id] = {
          ...next[user.id],
          show_in_about: isSelected,
          employee_order: ''
        };
      });

      orderedSelectedIds.forEach((userId, index) => {
        next[userId] = {
          ...next[userId],
          show_in_about: true,
          employee_order: index + 1
        };
      });

      return next;
    });

    showNotification('Selección y orden aplicados desde la opción 2.', 'success');
  };

  const columns = [
    { id: 'id', label: 'ID' },
    { id: 'photo', label: 'Foto', sortable: false },
    { id: 'name', label: 'Nombre' },
    { id: 'last_name', label: 'Apellidos' },
    { id: 'email', label: 'Email' },
    { id: 'activeEditor', label: 'Activo (Pide tu cita)', sortable: false },
    { id: 'showInAboutEditor', label: 'Mostrar en About Us', sortable: false },
    // { id: 'employeeOrderEditor', label: 'Orden About Us', sortable: false },
    { id: 'moveOrderEditor', label: 'Orden About Us', sortable: false },
    { id: 'servicesStr', label: 'Servicio' },
    { id: 'category', label: 'Categoría' },
    { id: 'rolesStr', label: 'Rol' },
  ];

  const orderedUsersForTable = useMemo(() => {
    const shownSet = new Set(orderedAboutIds);
    const byId = new Map(users.map(user => (user.id !== 1 ? [user.id, user] : null)).filter(Boolean));
    const shownUsers = orderedAboutIds.map(id => byId.get(id)).filter(Boolean);
    const nonShownUsers = usersWithoutRoot.filter(user => !shownSet.has(user.id));
    return [...shownUsers, ...nonShownUsers];
  }, [users, usersWithoutRoot, orderedAboutIds]);

  if (loading) return <Loader />;

  const rows = orderedUsersForTable.map(u => ({
    ...u,
    photo: (
      <Avatar
        src={utils.image_path(u.image || u.avatar)}
        alt={u.name}
        variant="rounded"
        sx={{ width: 48, height: 48, mx: 'auto' }}
      />
    ),
    last_name: u.last_name || u.lastName || '',
    activeEditor: u.id === 1 ? "" : (
      <Checkbox
        checked={Boolean(userEdits[u.id]?.active)}
        onChange={e => handleFieldChange(u, 'active', e.target.checked)}
        disabled={savingAll || u.id === 1}
      />
    ),
    showInAboutEditor: u.id === 1 ? "" : (
      <Checkbox
        checked={Boolean(userEdits[u.id]?.show_in_about)}
        onChange={e => handleFieldChange(u, 'show_in_about', e.target.checked)}
        disabled={savingAll || u.id === 1}
      />
    ),
    // employeeOrderEditor: u.id === 1 ? "" : (
    //   <TextField
    //     size="small"
    //     type="number"
    //     value={userEdits[u.id]?.employee_order ?? ''}
    //     onChange={e => handleFieldChange(u, 'employee_order', e.target.value)}
    //     inputProps={{ min: 1, max: Math.max(aboutUsersCount, 1) }}
    //     disabled={savingAll || u.id === 1 || !Boolean(userEdits[u.id]?.show_in_about)}
    //     sx={{ width: 90 }}
    //   />
    // ),
    moveOrderEditor: (() => {
      const position = orderedAboutIds.indexOf(u.id);
      const isInAbout = position !== -1;

      if (u.id === 1 || !isInAbout) return '—';

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <DragIndicatorIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ minWidth: 18, textAlign: 'center', fontWeight: 700 }}>
            {position + 1}
          </Typography>
        </Box>
      );
    })(),
    rolesStr: u.roles.map(r => r.name).join(' '),
    servicesStr: u.services?.map(s => s.name).join(', '),
    category:
      typeof u.category === 'string'
        ? u.category.charAt(0).toUpperCase() + u.category.slice(1)
        : u.category?.name
        ? u.category.name.charAt(0).toUpperCase() + u.category.name.slice(1)
        : ''
  }));

  const actions = usr => {
    if (usr.id === 1) return null;

    return (
      <>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1}}>
        <IconButton onClick={() => handleEdit(usr.id)} sx={{ color: ACTION_ICON_COLORS.edit }}>
          <EditIcon />
        </IconButton>
        {!isAdminUser(usr) && (
          <IconButton onClick={() => handleDelete(usr.id)} sx={{ color: ACTION_ICON_COLORS.delete }}>
            <DeleteIcon />
          </IconButton>
        )}
       </Box> 
      </>
    );
  };

  return (
    <Container maxWidth="xl" sx={INTRANET_PAGE_CONTAINER_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <PageTitle>Lista de Usuarios</PageTitle>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/registro')}>Añadir usuario</Button>
        </Box>
      </Box>
      <Box sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
        Máximo de orden calculado por usuarios con “Mostrar en About Us”: {aboutUsersCount}
      </Box>
      {/* <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Opción 1 (profesional): arrastra filas en la tabla para cambiar el orden de About Us.
        </Typography>
        <Button
          variant={advancedModeEnabled ? 'contained' : 'outlined'}
          onClick={() => setAdvancedModeEnabled(prev => !prev)}
        >
          {advancedModeEnabled ? 'Ocultar modo avanzado' : 'Abrir modo avanzado (opción 2)'}
        </Button>
      </Box> */}
      {advancedModeEnabled && (
        <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
          <AboutOrderSelectedDnD
            employees={aboutEmployees}
            selectedIds={selectedDnDIds}
            onSelectedChange={setSelectedDnDIds}
            orderIds={selectedDnDOrderIds}
            onOrderChange={setSelectedDnDOrderIds}
            onApply={applySelectedDnDOrder}
          />
        </Box>
      )}
      <DataTable
        columns={columns}
        rows={rows}
        actions={actions}
        disableSorting
        rowDragAndDrop={{
          enabled: true,
          isRowDraggable: row => orderedAboutIds.includes(row.id),
          getRowId: row => row.id,
          onRowDrop: moveByTableRowDrag
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&.MuiDataGrid-rowDragging': {
              cursor: 'grabbing',
            },
          }
        }}
        beforePagination={(
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant="contained"            
              onClick={handleSaveAll}
              disabled={savingAll}
            >
              Guardar todos los cambios ({modifiedEntries.length})
            </Button>
          </Box>
        )}
      />
      <Dialog
        open={deleteTargetUserId !== null}
        onClose={closeTransferDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reasignar datos antes de eliminar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Elige el usuario que recibirá clientes, artículos, formularios y demás datos.
          </Typography>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              maxHeight: 220,
              overflowY: 'auto',
              bgcolor: 'background.default'
            }}
          >
            {transferCandidates.map(user => (
              <Typography key={user.id} variant="body2" sx={{ mb: 0.5 }}>
                {user.id}: {`${user.name} ${user.last_name || user.lastName || ''}`.trim()}
              </Typography>
            ))}
          </Box>
          <TextField
            label="Escribe el ID"
            value={transferToUserIdInput}
            onChange={event => setTransferToUserIdInput(event.target.value)}
            type="number"
            fullWidth
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
          <Button onClick={closeTransferDialog} variant="outlined">Cancelar</Button>
          <Button onClick={confirmDeleteWithTransfer} variant="contained">Aceptar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
